import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle, Eye, EyeOff, AlertCircle, LogIn } from 'lucide-react';
import StudentLogo from '@/components/StudentLogo';

interface LoginFormData {
  email: string;
  institutionCode: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    institutionCode: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const { login } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Check for success message from email verification
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      if (location.state.email) {
        setFormData(prev => ({ ...prev, email: location.state.email }));
      }
    }
  }, [location.state]);

  const handleInputChange = (field: keyof LoginFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Validate all fields are filled
      if (!formData.email || !formData.institutionCode || !formData.password) {
        setError('Please fill in all fields');
        return;
      }

      // Try to login with email, password, and institution code
      await login(formData.email, formData.password, formData.institutionCode);
    } catch (error: any) {
      console.error('Login error:', error);

      // Check if this might be a first-time user issue
      const errorMessage = error.response?.data?.message || error.message || '';
      const isCredentialError = errorMessage.toLowerCase().includes('invalid') ||
                               errorMessage.toLowerCase().includes('credential') ||
                               errorMessage.toLowerCase().includes('unauthorized') ||
                               error.response?.status === 401;

      if (isCredentialError) {
        setError('Login failed. Logging in for the first time? Check with your institution administrator to find out about your enrollment and login process. If you already have an account, please verify your email, institution code, and password.');
      } else {
        setError(errorMessage || 'Login failed. Please try again or contact your administrator for assistance.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Consolidated Login Form
  const renderLoginForm = () => (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <StudentLogo size="xl" showText={true} textColor="default" />
          </div>
          <p className="mt-2 text-sm text-muted-foreground font-body">
            Sign in to access your account
          </p>
        </div>

        <div className="mt-8 bg-card py-8 px-6 shadow-xl rounded-2xl border border-border">
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                <span className="text-sm text-green-700 font-medium">{successMessage}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3" />
                <span className="text-sm text-red-700 font-medium">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="block text-sm font-semibold text-foreground font-heading">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                autoFocus
                className="font-body"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="institutionCode" className="block text-sm font-semibold text-foreground font-heading">
                Institution Code
              </Label>
              <Input
                id="institutionCode"
                type="text"
                placeholder="Enter your institution code"
                value={formData.institutionCode}
                onChange={(e) => handleInputChange('institutionCode', e.target.value.toUpperCase())}
                required
                className="font-body"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="block text-sm font-semibold text-foreground font-heading">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  required
                  className="font-body pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-primary hover:text-primary/80 transition-colors duration-200"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              disabled={isLoading || !formData.email.trim() || !formData.institutionCode.trim() || !formData.password.trim()}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                  <span className="font-medium">Signing you in...</span>
                </>
              ) : (
                <><LogIn className="mr-2 h-5 w-5" aria-hidden="true" />Sign In</>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors duration-200 font-body"
            >
              Forgot your password?
            </Link>
          </div>

          {/* Version Display */}
          <div className="mt-6 text-center text-xs text-muted-foreground font-body">
            Student Portal v1.0.0
          </div>
        </div>
      </div>
    </div>
  );



  return renderLoginForm();
};

export default LoginPage;
